import { differenceInCalendarMonths, format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import ReactDatePicker, { type ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { cn } from '@helpers/cn';
import { FormFieldDatePickerHeader } from '@components/core-ui/form-elements/FormFieldDatePickerHeader';
import { FormFieldDatePickerHeaderMonth } from '@components/core-ui/form-elements/FormFieldDatePickerHeaderMonth';
import { FormFieldDatePickerRoot } from '@components/core-ui/form-elements/FormFieldDatePickerRoot';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { FormFieldIcon } from '@components/core-ui/form-elements/FormFieldIcon';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import CalendarIcon from '@components/icons/Calendar.svg';
import CloseIcon from '@components/icons/Close.svg';
import LeftIcon from '@components/icons/ArrowLeft.svg';
import RightIcon from '@components/icons/ArrowRight.svg';
import {
  datePickerPopperClassName,
  dateRangePickerPopperModifiers,
  datePickerPopperPlacement,
} from '../DatePicker/helpers/datePickerPopperConfig';
import { useDatePickerOpenState } from '../DatePicker/helpers/useDatePickerOpenState';
import { useMediaQuery } from './helpers/useMediaQuery';
import { monthsList } from '../TextDatePicker/helpers/monthsList';
import { triggerVariants } from './styles';
import { TDateRange, TProps } from './types/TProps';
import '../DatePicker/styles/date-picker-popper.css';
import './styles/date-range-popper.css';

const formatRange = (range: TDateRange, label: string): string => {
  const [start, end] = range;
  if (!start && !end) {
    return label;
  }
  if (start && !end) {
    return format(start, 'd MMM yyyy');
  }
  if (!start && end) {
    return `... - ${format(end, 'd MMM yyyy')}`;
  }
  if (start && end) {
    return `${format(start, 'd MMM yyyy')} - ${format(end, 'd MMM yyyy')}`;
  }
  return label;
};

const normalizeRange = ([start, end]: TDateRange): TDateRange => {
  if (!start || !end || start.getTime() <= end.getTime()) {
    return [start, end];
  }
  return [end, start];
};

export const DateRangePicker = ({
  label,
  value,
  onChange,
  minDate,
  maxDate = new Date(),
  disabled = false,
  error = false,
  size = 'md',
  className = '',
  name = 'date-range-picker',
}: TProps) => {
  const [draft, setDraft] = useState<TDateRange>(value);
  const isWideViewport = useMediaQuery('(min-width: 768px)');
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const { isOpen, placement, handleCalendarOpen, handleCalendarClose } = useDatePickerOpenState(wrapperRef);
  const startTimestamp = value[0]?.getTime();
  const endTimestamp = value[1]?.getTime();

  useEffect(() => {
    setDraft([
      startTimestamp === undefined ? null : new Date(startTimestamp),
      endTimestamp === undefined ? null : new Date(endTimestamp),
    ]);
  }, [startTimestamp, endTimestamp]);

  const handleChange = (range: TDateRange) => {
    const normalizedRange = normalizeRange(range);
    setDraft(normalizedRange);
    const [start, end] = normalizedRange;
    if ((start && end) || (!start && !end)) {
      onChange(normalizedRange);
    }
  };

  const handleClear = () => {
    setDraft([null, null]);
    onChange([null, null]);
    wrapperRef.current?.querySelector<HTMLButtonElement>('button[aria-haspopup="dialog"]')?.focus();
  };

  const hasValue = Boolean(draft[0] || draft[1]);
  const state = disabled ? 'disabled' : error ? 'error' : 'default';
  const rangeText = formatRange(draft, label);
  const triggerName = hasValue ? `${label}: ${rangeText}` : label;
  const selectionStatus =
    isOpen && draft[0] && !draft[1]
      ? `Start date ${format(draft[0], 'd MMMM yyyy')} selected. Choose an end date.`
      : '';

  return (
    <FormFieldRoot disabled={disabled} className="w-full">
      <FormFieldDatePickerRoot ref={wrapperRef} data-open={isOpen} data-placement={placement ?? 'bottom'}>
        {hasValue ? (
          <FormFieldIcon clickable className="right-5 h-auto w-auto">
            <IconButtonClean
              type="button"
              aria-label="Clear date range"
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                handleClear();
              }}
            >
              <CloseIcon />
            </IconButtonClean>
          </FormFieldIcon>
        ) : (
          <FormFieldIcon>
            <CalendarIcon aria-hidden="true" focusable="false" />
          </FormFieldIcon>
        )}

        <ReactDatePicker
          name={name}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          monthsShown={isWideViewport ? 2 : 1}
          selectsRange
          swapRange
          preventOpenOnFocus
          startDate={draft[0]}
          endDate={draft[1]}
          popperClassName={`${datePickerPopperClassName} autoscript-daterange-popper`}
          popperModifiers={dateRangePickerPopperModifiers}
          popperPlacement={datePickerPopperPlacement}
          popperProps={{ strategy: 'fixed' }}
          onCalendarOpen={handleCalendarOpen}
          onCalendarClose={handleCalendarClose}
          renderCustomHeader={({
            monthDate,
            customHeaderCount,
            decreaseMonth,
            increaseMonth,
          }: ReactDatePickerCustomHeaderProps) => (
            <FormFieldDatePickerHeader>
              {customHeaderCount === 0 && !(minDate && differenceInCalendarMonths(monthDate, minDate) <= 0) ? (
                <IconButtonClean type="button" aria-label="Previous month" onClick={decreaseMonth}>
                  <LeftIcon aria-hidden="true" focusable="false" />
                </IconButtonClean>
              ) : (
                <span className="w-6" aria-hidden="true" />
              )}
              <FormFieldDatePickerHeaderMonth>
                {monthsList[monthDate.getMonth()]} {monthDate.getFullYear()}
              </FormFieldDatePickerHeaderMonth>
              {customHeaderCount === (isWideViewport ? 1 : 0) && differenceInCalendarMonths(maxDate, monthDate) > 0 ? (
                <IconButtonClean type="button" aria-label="Next month" onClick={increaseMonth}>
                  <RightIcon aria-hidden="true" focusable="false" />
                </IconButtonClean>
              ) : (
                <span className="w-6" aria-hidden="true" />
              )}
            </FormFieldDatePickerHeader>
          )}
          customInput={
            <button
              type="button"
              className={cn(triggerVariants({ size, state }), className)}
              aria-label={triggerName}
              aria-haspopup="dialog"
              aria-expanded={isOpen}
              aria-invalid={error || undefined}
              data-open={isOpen}
              data-placement={placement ?? 'bottom'}
            >
              {rangeText}
            </button>
          }
          onChange={handleChange}
        />
        <span className="sr-only" role="status" aria-live="polite">
          {selectionStatus}
        </span>
      </FormFieldDatePickerRoot>
    </FormFieldRoot>
  );
};
